import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AuthLayout from "@/components/AuthLayout";
import GoogleButton from "@/components/GoogleButton";
import { Loader2 } from "lucide-react";

export default function Login() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
      navigate(data.role === "admin" ? "/admin" : "/dashboard");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      title="Welcome back"
      subtitle="Sign in to access AI jobs, internships and projects."
      footer={<>New here? <Link to="/register" className="text-[#7A222B] font-semibold hover:underline" data-testid="go-register">Create an account</Link></>}
    >
      <form onSubmit={submit} className="space-y-4">
        {error && <div data-testid="login-error" className="bg-[#FAF0F1] border border-[#D99B9F] text-[#7A222B] text-sm rounded-xl px-4 py-3">{error}</div>}
        <div>
          <label className="text-sm font-medium text-[#2D2825]">Email</label>
          <input
            type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            data-testid="login-email"
            className="mt-1.5 w-full rounded-xl border border-[#EAE3DB] bg-white px-4 py-3 text-[#2D2825] focus:outline-none focus:border-[#7A222B] focus:ring-2 focus:ring-[#7A222B]/10 transition"
            placeholder="you@college.edu"
          />
        </div>
        <div>
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-[#2D2825]">Password</label>
            <Link to="/forgot-password" className="text-xs text-[#7A222B] hover:underline" data-testid="forgot-password-link">Forgot password?</Link>
          </div>
          <input
            type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
            data-testid="login-password"
            className="mt-1.5 w-full rounded-xl border border-[#EAE3DB] bg-white px-4 py-3 text-[#2D2825] focus:outline-none focus:border-[#7A222B] focus:ring-2 focus:ring-[#7A222B]/10 transition"
            placeholder="••••••••"
          />
        </div>
        <button
          type="submit" disabled={loading} data-testid="login-submit-btn"
          className="w-full flex items-center justify-center gap-2 bg-[#7A222B] hover:bg-[#5C1D24] text-white font-semibold py-3 rounded-xl transition-colors disabled:opacity-60"
        >
          {loading && <Loader2 className="w-4 h-4 animate-spin" />} Sign in
        </button>
      </form>
      <div className="flex items-center gap-4 my-6">
        <div className="h-px bg-[#EAE3DB] flex-1" />
        <span className="text-xs text-[#948A80]">or</span>
        <div className="h-px bg-[#EAE3DB] flex-1" />
      </div>
      <GoogleButton label="Sign in with Google" />
    </AuthLayout>
  );
}
